import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { BrandMark } from '@/components/common/Layout/BrandMark';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string[];
  fallback?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  fallback,
}) => {
  const { state } = useAuth();
  const location = useLocation();

  // Показываем загрузку пока проверяется аутентификация
  if (state.isLoading) {
    return (
      fallback || (
        <div className="flex min-h-dvh items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4">
            <BrandMark className="h-10 w-10 animate-pulse rounded-xl" />
            <p className="text-sm text-muted-foreground">Загрузка…</p>
          </div>
        </div>
      )
    );
  }

  // Если пользователь не аутентифицирован, перенаправляем на логин
  if (!state.isAuthenticated || !state.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Проверка ролей, если они указаны
  if (requiredRole && requiredRole.length > 0) {
    if (!requiredRole.includes(state.user.role)) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl tone-warning ring-1 ring-inset">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold tracking-tight">Доступ ограничен</h2>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            У вашей роли недостаточно прав для этого раздела. Если доступ нужен для работы — обратитесь к
            администратору.
          </p>
          <Button asChild variant="outline" className="mt-5">
            <Link to="/dashboard">Вернуться на главную</Link>
          </Button>
        </div>
      );
    }
  }

  return <>{children}</>;
};
